// src/pages/transport/TransportBookingStepper.tsx
import React, { useMemo, useState } from "react";
import axios from "axios";
import {
    Stepper,
    Group,
    Button,
    Stack,
    Text,
    Alert,
    Divider,
    Paper,
    SimpleGrid,
    Select,
    TextInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useMediaQuery } from "@mantine/hooks";
import {
    TransportInitResponseDto,
    CreateTransportBookingRequestDto,
} from "./types";
import { WHATSAPP_COUNTRIES } from "../../data/whatsappCountries";
import { useLanguage } from "../../context/LanguageContext";

type Props = {
    init: TransportInitResponseDto;
    token: string;
};

type Draft = {
    pickupLocationId?: number;
    dropoffLocationId?: number;
    pickupDate?: string;
    pickupTime?: string;
    passengerCount: number;

    whatsappCountryIso2?: string;
    whatsappCountryCode?: string;
    whatsappNumber?: string;

    contactName?: string;
    contactEmail?: string;
};

const translations = {
    da: {
        errorTitle: "Fejl",
        loadTimesError: "Kunne ikke hente tider.",
        missingBookingData: "Mangler data i bestillingen.",
        invalidContact: "Udfyld kontaktnavn og gyldig email.",
        invalidWhatsapp: "Udfyld gyldigt WhatsApp-nummer.",
        createBookingError: "Kunne ikke oprette booking.",
        orderCreatedTitle: "Bestilling oprettet",
        orderCreatedMessage: (id: number) =>
            `Din transport er registreret (ID: ${id}).`,

        confirmed: "Bekræftet ✅",
        thankYou: "Tak — din bestilling er registreret.",
        contact: "Kontakt",
        event: "Event",
        name: "Navn",
        email: "Email",
        whatsappNumber: "WhatsApp-nummer",
        countryCode: "Landekode",
        selectCountryCode: "Vælg landekode",
        selectedCountry: "Valgt land",
        unknown: "Ukendt",
        whatsappPlaceholder: "f.eks. 12345678",
        whatsappHelp:
            "Vælg landekode og indtast dit WhatsApp-nummer for at fortsætte.",

        contactDetails: "Kontaktoplysninger",
        contactName: "Kontaktperson",
        contactNamePlaceholder: "Dit fulde navn",
        emailPlaceholder: "navn@eksempel.dk",
        validEmail: "Indtast en gyldig emailadresse.",

        notEntered: "Ikke udfyldt",
        notSelected: "Ikke valgt",

        pickUp: "Afhentning",
        pickUpQuestion: "Hvor skal du hentes?",

        dropOff: "Afsætning",
        dropOffQuestion: "Hvor skal du hen?",

        day: "Dag",
        selectDay: "Vælg dag",

        time: "Tid",
        selectTimeSlot: "Vælg et tidspunkt",
        loadingTimes: "Indlæser tider…",
        noTimes: "Ingen ledige tider på den valgte dag.",

        persons: "Personer",
        howManyPeople: "Hvor mange personer?",

        back: "Tilbage",
        next: "Næste",
        approveOrder: "Godkend bestilling",

        popular: "Populære",
        allCountries: "Alle lande",
    },
    en: {
        errorTitle: "Error",
        loadTimesError: "Could not load times.",
        missingBookingData: "Missing booking data.",
        invalidContact: "Please fill in contact name and a valid email.",
        invalidWhatsapp: "Please enter a valid WhatsApp number.",
        createBookingError: "Could not create booking.",
        orderCreatedTitle: "Order created",
        orderCreatedMessage: (id: number) =>
            `Your transport has been registered (ID: ${id}).`,

        confirmed: "Confirmed ✅",
        thankYou: "Thank you — your order has been registered.",
        contact: "Contact",
        event: "Event",
        name: "Name",
        email: "Email",
        whatsappNumber: "WhatsApp number",
        countryCode: "Country code",
        selectCountryCode: "Select country code",
        selectedCountry: "Selected country",
        unknown: "Unknown",
        whatsappPlaceholder: "e.g. 12345678",
        whatsappHelp:
            "Please select a country code and enter your WhatsApp number to continue.",

        contactDetails: "Contact details",
        contactName: "Contact name",
        contactNamePlaceholder: "Your full name",
        emailPlaceholder: "name@example.com",
        validEmail: "Enter a valid email address.",

        notEntered: "Not entered",
        notSelected: "Not selected",

        pickUp: "Pick Up",
        pickUpQuestion: "Where should you be picked up?",

        dropOff: "Drop Off",
        dropOffQuestion: "Where are you going?",

        day: "Day",
        selectDay: "Select day",

        time: "Time",
        selectTimeSlot: "Select a time slot",
        loadingTimes: "Loading times…",
        noTimes: "No available times for the selected day.",

        persons: "Persons",
        howManyPeople: "How many people?",

        back: "Back",
        next: "Next",
        approveOrder: "Approve order",

        popular: "Popular",
        allCountries: "All countries",
    },
};

function OptionGrid({
    options,
    value,
    onChange,
    cols,
}: {
    options: { value: string; label: string }[];
    value: string | null;
    onChange: (v: string) => void;
    cols?: { base: number; sm: number };
}) {
    return (
        <SimpleGrid cols={cols ?? { base: 1, sm: 2 }} spacing="sm">
            {options.map((o) => {
                const selected = value === o.value;

                return (
                    <Paper
                        key={o.value}
                        withBorder
                        p="md"
                        radius="md"
                        style={{
                            cursor: "pointer",
                            borderColor: selected
                                ? "var(--mantine-primary-color-filled)"
                                : undefined,
                            boxShadow: selected
                                ? "0 0 0 1px var(--mantine-primary-color-filled)"
                                : undefined,
                        }}
                        onClick={() => onChange(o.value)}
                    >
                        <Text fw={600}>{o.label}</Text>
                    </Paper>
                );
            })}
        </SimpleGrid>
    );
}

export default function TransportBookingStepper({ init, token }: Props) {
    const [active, setActive] = useState(0);
    const [draft, setDraft] = useState<Draft>({ passengerCount: 1 });
    const [times, setTimes] = useState<string[]>([]);
    const [loadingTimes, setLoadingTimes] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isMobile = useMediaQuery("(max-width: 768px)");
    const isPublicBooking = init.isPublicBooking;

    const { language } = useLanguage();
    const currentLanguage = token === "new" ? "da" : "en";
    console.log("CL:" + currentLanguage)
    console.log("T:" + token)
    const t = translations[currentLanguage];

    const PRIORITY_ISO2 = [
        "DK",
        "CA",
        "CN",
        "FR",
        "DE",
        "HK",
        "IN",
        "ID",
        "JP",
        "MY",
        "MX",
        "SG",
        "ES",
        "TW",
        "TH",
        "TR",
        "GB",
        "VN",
        "BG",
    ];

    const pickupOptions = useMemo(
        () =>
            init.pickupLocations.map((l) => ({
                value: String(l.id),
                label: l.name,
            })),
        [init.pickupLocations]
    );

    const dropoffOptions = useMemo(
        () =>
            init.dropoffLocations.map((l) => ({
                value: String(l.id),
                label: l.name,
            })),
        [init.dropoffLocations]
    );

    const availableDates = useMemo(() => {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        const tomorrowStart = new Date(todayStart);
        tomorrowStart.setDate(todayStart.getDate() + 1);

        const max = new Date(todayStart);
        max.setDate(todayStart.getDate() + init.bookingMaxDaysAhead);

        return init.timeWindows
            .map((w) => w.date)
            .filter((dateStr) => {
                const d = new Date(dateStr + "T00:00:00");
                return d >= tomorrowStart && d <= max;
            });
    }, [init.timeWindows, init.bookingMaxDaysAhead]);

    const dateOptions = useMemo(
        () => availableDates.map((d) => ({ value: d, label: d })),
        [availableDates]
    );

    const pickupName =
        init.pickupLocations.find((x) => x.id === draft.pickupLocationId)?.name ??
        null;

    const dropoffName =
        init.dropoffLocations.find((x) => x.id === draft.dropoffLocationId)?.name ??
        null;

    const passengersValid =
        draft.passengerCount >= 1 && draft.passengerCount <= 30;

    const whatsappCountryOptions = useMemo(() => {
        const prioritySet = new Set(PRIORITY_ISO2);

        const popularItems = WHATSAPP_COUNTRIES
            .filter((c) => prioritySet.has(c.iso2))
            .sort(
                (a, b) =>
                    PRIORITY_ISO2.indexOf(a.iso2) - PRIORITY_ISO2.indexOf(b.iso2)
            )
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        const restItems = WHATSAPP_COUNTRIES
            .filter((c) => !prioritySet.has(c.iso2))
            .sort((a, b) => a.country.localeCompare(b.country))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        return [
            { group: t.popular, items: popularItems },
            { group: t.allCountries, items: restItems },
        ];
    }, [t.popular, t.allCountries]);

    const selectedWhatsappCountry =
        WHATSAPP_COUNTRIES.find((x) => x.code === draft.whatsappCountryCode)
            ?.country ?? null;

    const whatsappValid =
        !!draft.whatsappCountryCode &&
        !!draft.whatsappNumber &&
        draft.whatsappNumber.replace(/\s+/g, "").length >= 6;

    const emailValid = !!draft.contactEmail?.trim()
        ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.contactEmail.trim())
        : false;

    const contactValid = !isPublicBooking
        ? true
        : !!draft.contactName?.trim() && emailValid;

    const isStepValid = (step: number) => {
        switch (step) {
            case 0:
                return contactValid && whatsappValid;
            case 1:
                return !!draft.pickupLocationId;
            case 2:
                return !!draft.dropoffLocationId;
            case 3:
                return !!draft.pickupDate;
            case 4:
                return !!draft.pickupTime;
            case 5:
                return passengersValid;
            default:
                return true;
        }
    };

    const ensureTimeslots = async () => {
        if (!draft.pickupDate) return;

        try {
            setLoadingTimes(true);
            setError(null);

            const apiUrl = process.env.REACT_APP_API_URL;

            const params: Record<string, string> = {
                token,
                date: draft.pickupDate,
            };

            if (isPublicBooking) {
                params.eventId = String(init.event.eventId);
            }

            const res = await axios.get(
                `${apiUrl}/api/transport/booking/timeslots`,
                { params }
            );
            setTimes(res.data.times ?? []);
        } catch (e: any) {
            setError(e?.response?.data ?? t.loadTimesError);
        } finally {
            setLoadingTimes(false);
        }
    };

    const next = async () => {
        if (active === 3) {
            await ensureTimeslots();
        }

        if (active < 6 && isStepValid(active)) {
            setActive((a) => a + 1);
        }
    };

    const prev = () => setActive((a) => Math.max(0, a - 1));

    const submit = async () => {
        if (
            !draft.pickupLocationId ||
            !draft.dropoffLocationId ||
            !draft.pickupDate ||
            !draft.pickupTime
        ) {
            setError(t.missingBookingData);
            return;
        }

        if (isPublicBooking && !contactValid) {
            setError(t.invalidContact);
            return;
        }

        if (!whatsappValid) {
            setError(t.invalidWhatsapp);
            return;
        }

        const payload: CreateTransportBookingRequestDto = {
            Token: token,
            EventID: isPublicBooking ? init.event.eventId : undefined,

            PickupLocationId: draft.pickupLocationId,
            DropoffLocationId: draft.dropoffLocationId,
            PickupDate: draft.pickupDate,
            PickupTime: draft.pickupTime,
            PassengerCount: draft.passengerCount,

            WhatsappCountryCode: draft.whatsappCountryCode,
            WhatsappNumber: draft.whatsappNumber,

            ContactName: isPublicBooking ? draft.contactName?.trim() : undefined,
            ContactEmail: isPublicBooking ? draft.contactEmail?.trim() : undefined,
        };

        try {
            setSubmitLoading(true);
            setError(null);

            const apiUrl = process.env.REACT_APP_API_URL;

            const res = await axios.post(
                `${apiUrl}/api/transport/booking`,
                payload
            );

            showNotification({
                title: t.orderCreatedTitle,
                message: t.orderCreatedMessage(res.data.transportBookingId),
            });

            setActive(6);
        } catch (e: any) {
            setError(e?.response?.data ?? t.createBookingError);
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Stack>
            {error && (
                <Alert color="red" title={t.errorTitle}>
                    {error}
                </Alert>
            )}

            <Stepper
                active={active}
                onStepClick={setActive}
                allowNextStepsSelect={false}
                orientation={isMobile ? "vertical" : "horizontal"}
                size={isMobile ? "sm" : "md"}
                styles={{
                    root: isMobile
                        ? {
                            overflowX: "auto",
                            WebkitOverflowScrolling: "touch",
                            paddingBottom: 6,
                        }
                        : undefined,
                    steps: isMobile ? { flexWrap: "nowrap", gap: 16 } : undefined,
                    step: isMobile ? { minWidth: 110 } : undefined,
                }}
            >
                <Stepper.Step
                    label={t.contact}
                    description={
                        isMobile
                            ? isPublicBooking
                                ? draft.contactName?.trim() || t.notEntered
                                : init.person?.name || t.notSelected
                            : undefined
                    }
                >
                    <Paper withBorder p="md" radius="md">
                        {isPublicBooking ? (
                            <>
                                <Text fw={600} mb="xs">
                                    {t.contactDetails}
                                </Text>

                                <Stack gap="sm">
                                    <TextInput
                                        label={t.contactName}
                                        placeholder={t.contactNamePlaceholder}
                                        value={draft.contactName ?? ""}
                                        onChange={(e) =>
                                            setDraft((d) => ({
                                                ...d,
                                                contactName: e.currentTarget.value,
                                            }))
                                        }
                                        required
                                    />

                                    <TextInput
                                        label={t.email}
                                        type="email"
                                        placeholder={t.emailPlaceholder}
                                        value={draft.contactEmail ?? ""}
                                        onChange={(e) =>
                                            setDraft((d) => ({
                                                ...d,
                                                contactEmail: e.currentTarget.value,
                                            }))
                                        }
                                        required
                                    />

                                    {!!draft.contactEmail && !emailValid && (
                                        <Text size="sm" c="red">
                                            {t.validEmail}
                                        </Text>
                                    )}
                                </Stack>

                                <Divider my="sm" />
                            </>
                        ) : (
                            <>
                                <Text>
                                    <b>{t.name}:</b> {init.person?.name ?? "-"}
                                </Text>
                                {init.person?.email && (
                                    <Text>
                                        <b>{t.email}:</b> {init.person.email}
                                    </Text>
                                )}
                                <Divider my="sm" />
                            </>
                        )}

                        <Text>
                            <b>{t.event}:</b> {init.event.name}
                        </Text>

                        <Divider my="sm" />

                        <Text fw={600} mb="xs">
                            {t.whatsappNumber}
                        </Text>

                        <Stack gap="sm">
                            <Select
                                label={t.countryCode}
                                placeholder={t.selectCountryCode}
                                value={draft.whatsappCountryIso2 ?? null}
                                onChange={(iso2) => {
                                    const c = WHATSAPP_COUNTRIES.find(
                                        (x) => x.iso2 === iso2
                                    );

                                    setDraft((d) => ({
                                        ...d,
                                        whatsappCountryIso2: iso2 ?? undefined,
                                        whatsappCountryCode: c?.code ?? "",
                                    }));
                                }}
                                data={whatsappCountryOptions}
                                searchable
                                required
                            />

                            {draft.whatsappCountryCode && (
                                <Text size="sm" c="dimmed">
                                    {t.selectedCountry}:{" "}
                                    <b>{selectedWhatsappCountry ?? t.unknown}</b>
                                </Text>
                            )}

                            <TextInput
                                label={t.whatsappNumber}
                                placeholder={t.whatsappPlaceholder}
                                value={draft.whatsappNumber ?? ""}
                                onChange={(e) => {
                                    const raw = e.currentTarget.value;
                                    const cleaned = raw.replace(/[^\d\s-]/g, "");
                                    setDraft((d) => ({
                                        ...d,
                                        whatsappNumber: cleaned,
                                    }));
                                }}
                                required
                            />

                            {!whatsappValid && (
                                <Text size="sm" c="red">
                                    {t.whatsappHelp}
                                </Text>
                            )}
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label={t.pickUp}
                    description={isMobile ? pickupName ?? t.notSelected : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            {t.pickUpQuestion}
                        </Text>
                        <OptionGrid
                            options={pickupOptions}
                            value={
                                draft.pickupLocationId
                                    ? String(draft.pickupLocationId)
                                    : null
                            }
                            onChange={(v) =>
                                setDraft((d) => ({
                                    ...d,
                                    pickupLocationId: Number(v),
                                }))
                            }
                        />
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label={t.dropOff}
                    description={isMobile ? dropoffName ?? t.notSelected : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            {t.dropOffQuestion}
                        </Text>
                        <OptionGrid
                            options={dropoffOptions}
                            value={
                                draft.dropoffLocationId
                                    ? String(draft.dropoffLocationId)
                                    : null
                            }
                            onChange={(v) =>
                                setDraft((d) => ({
                                    ...d,
                                    dropoffLocationId: Number(v),
                                }))
                            }
                        />
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label={t.day}
                    description={isMobile ? draft.pickupDate ?? t.notSelected : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            {t.selectDay}
                        </Text>
                        <OptionGrid
                            options={dateOptions}
                            value={draft.pickupDate ?? null}
                            onChange={(v) =>
                                setDraft((d) => ({
                                    ...d,
                                    pickupDate: v,
                                    pickupTime: undefined,
                                }))
                            }
                        />
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label={t.time}
                    description={isMobile ? draft.pickupTime ?? t.notSelected : undefined}
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            {t.selectTimeSlot}
                        </Text>

                        {loadingTimes ? (
                            <Text>{t.loadingTimes}</Text>
                        ) : times.length === 0 ? (
                            <Text c="dimmed">{t.noTimes}</Text>
                        ) : (
                            <SimpleGrid cols={{ base: 2, sm: 4 }}>
                                {times.map((tItem) => {
                                    const selected = draft.pickupTime === tItem;

                                    return (
                                        <Paper
                                            key={tItem}
                                            withBorder
                                            p="sm"
                                            radius="md"
                                            style={{
                                                cursor: "pointer",
                                                textAlign: "center",
                                                borderColor: selected
                                                    ? "var(--mantine-primary-color-filled)"
                                                    : undefined,
                                                boxShadow: selected
                                                    ? "0 0 0 1px var(--mantine-primary-color-filled)"
                                                    : undefined,
                                            }}
                                            onClick={() =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    pickupTime: tItem,
                                                }))
                                            }
                                        >
                                            <Text fw={600}>{tItem}</Text>
                                        </Paper>
                                    );
                                })}
                            </SimpleGrid>
                        )}
                    </Paper>
                </Stepper.Step>

                <Stepper.Step
                    label={t.persons}
                    description={
                        isMobile
                            ? String(draft.passengerCount ?? t.notSelected)
                            : undefined
                    }
                >
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            {t.howManyPeople}
                        </Text>

                        <SimpleGrid cols={{ base: 5, sm: 10 }}>
                            {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => {
                                const selected = draft.passengerCount === n;

                                return (
                                    <Paper
                                        key={n}
                                        withBorder
                                        p="xs"
                                        radius="md"
                                        style={{
                                            cursor: "pointer",
                                            textAlign: "center",
                                            borderColor: selected
                                                ? "var(--mantine-primary-color-filled)"
                                                : undefined,
                                            boxShadow: selected
                                                ? "0 0 0 1px var(--mantine-primary-color-filled)"
                                                : undefined,
                                        }}
                                        onClick={() =>
                                            setDraft((d) => ({
                                                ...d,
                                                passengerCount: n,
                                            }))
                                        }
                                    >
                                        <Text fw={600}>{n}</Text>
                                    </Paper>
                                );
                            })}
                        </SimpleGrid>
                    </Paper>
                </Stepper.Step>

                <Stepper.Completed>
                    <Paper withBorder p="md" radius="md">
                        <Text fw={700}>{t.confirmed}</Text>
                        <Text>{t.thankYou}</Text>

                        {isPublicBooking && draft.contactName && (
                            <Text>
                                {t.contact}: {draft.contactName}
                                {draft.contactEmail ? ` (${draft.contactEmail})` : ""}
                            </Text>
                        )}

                        <Text>
                            WhatsApp: {draft.whatsappCountryCode} {draft.whatsappNumber}
                        </Text>
                    </Paper>
                </Stepper.Completed>
            </Stepper>

            {active < 6 && (
                <Group justify="space-between" mt="md">
                    <Button variant="default" onClick={prev} disabled={active === 0}>
                        {t.back}
                    </Button>

                    {active === 5 ? (
                        <Button onClick={submit} loading={submitLoading}>
                            {t.approveOrder}
                        </Button>
                    ) : (
                        <Button onClick={next} disabled={!isStepValid(active)}>
                            {t.next}
                        </Button>
                    )}
                </Group>
            )}
        </Stack>
    );
}